import React from "react";
import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import {
  inviteCalloutSurfaceSx,
  inviteIconSx,
  inviteTextBoldSx,
  inviteTextSx,
} from "./inviteTypography";

export type InviteCalloutVariant = "neutral" | "caution" | "error";

function calloutIcon(variant: InviteCalloutVariant) {
  const sx = { fontSize: 20, ...inviteIconSx };
  switch (variant) {
    case "error":
      return <ErrorOutline sx={sx} />;
    case "caution":
      return <WarningAmberOutlined sx={sx} />;
    default:
      return <InfoOutlined sx={sx} />;
  }
}

export interface InviteCalloutProps {
  variant?: InviteCalloutVariant;
  hideIcon?: boolean;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const InviteCallout: React.FC<InviteCalloutProps> = ({
  variant = "neutral",
  hideIcon = false,
  children,
  sx,
}) => (
  <Box
    sx={[
      (theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        textAlign: "left",
        borderRadius: `${theme.shape.borderRadius}px`,
        px: 2,
        py: 1.5,
        width: "100%",
        boxSizing: "border-box",
        ...inviteCalloutSurfaceSx(theme, variant),
      }),
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {!hideIcon && (
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {calloutIcon(variant)}
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

export interface InviteCalloutStepsProps {
  title: string;
  steps: string[];
}

export const InviteCalloutSteps: React.FC<InviteCalloutStepsProps> = ({
  title,
  steps,
}) => (
  <InviteCallout hideIcon>
    <Typography variant="body2" sx={{ ...inviteTextBoldSx, mb: 1 }}>
      {title}
    </Typography>
    <Box
      component="ol"
      sx={{
        m: 0,
        pl: 2.75,
        listStyle: "decimal",
        listStylePosition: "outside",
        ...inviteTextSx,
        "& li": {
          typography: "body2",
          mb: 0.5,
          display: "list-item",
          pl: 0.5,
        },
        "& li:last-of-type": { mb: 0 },
      }}
    >
      {steps.map((step, index) => (
        <li key={`${index}-${step}`}>{step}</li>
      ))}
    </Box>
  </InviteCallout>
);

export default InviteCallout;
